import { useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';

import { checkoutsApi } from '../../../api/checkouts';
import { ICheckoutPageSettings } from '../../../api/checkouts/types/checkout-page.type';
import { PageBlocks, useSeoSettings } from '../components/PageBlocks';
import { useCheckoutTracking } from '../components/PageBlocks/tracking';
import {
	isPreviewMode,
	usePreviewBridge,
	usePreviewLayoutReporter
} from '../components/PageBlocks/use-preview-bridge';
import './styles.scss';

const { findPricingPage } = checkoutsApi;

/**
 * Tela de planos pública ({platform}/planos/{slug}) — página de venda de
 * assinaturas montada no builder. A Tabela de planos (bloco pricingTable)
 * leva cada CTA ao link real do plano; os demais blocos são os mesmos do
 * checkout. Suporta o preview do builder (?_preview=1).
 */
export const PricingPageView = () => {
	const { slug } = useParams();

	const { data: fetched } = useQuery(
		[findPricingPage.key, { slug: slug ?? '' }],
		findPricingPage.fetch,
		{ refetchOnWindowFocus: false, retry: 1 }
	);

	const draft = usePreviewBridge();
	const page = draft ?? fetched;

	usePreviewLayoutReporter(!!page);

	const settings: ICheckoutPageSettings = useMemo(
		() => page?.settings ?? {},
		[page?.settings]
	);

	// Tema por página (mesma precedência do checkout).
	useEffect(() => {
		const theme = settings?.theme;
		if (!theme) return;
		const prefersDark =
			theme === 'dark' ||
			(theme === 'auto' &&
				window.matchMedia?.('(prefers-color-scheme: dark)').matches);
		document.body.dataset.themeOverride = theme;
		document.body.classList.toggle('dark-mode', prefersDark);
		return () => {
			delete document.body.dataset.themeOverride;
		};
	}, [settings?.theme]);

	useSeoSettings(settings);
	// Pixels desta página: PageView (a compra é rastreada no checkout).
	useCheckoutTracking(settings?.tracking, 'landing');

	return (
		<div className="pricing-page">
			<div className="container">
				<PageBlocks page={page} />
				{isPreviewMode() && !page?.blocks?.length && (
					<div className="page-blocks" style={{ minHeight: 160 }} />
				)}
			</div>
		</div>
	);
};
